/**
 * ResourceCenter
 * Main page for the Resource Center — videos, guides, and tutorials.
 * Accessible at /app/resources. No permission guard required.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PlayCircleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathDualSpinner,
} from '../../shared/OathUI';
import { useResources, useResourceDetail } from './hooks/useResources';
import ResourceCard from './components/ResourceCard';
import VideoPlayerModal from './components/VideoPlayerModal';
import GuideDetailModal from './components/GuideDetailModal';

// -------------------------------------------------------
// Filter tab config
// -------------------------------------------------------
const VALID_TYPES = ['new', 'all', 'video', 'guide'];
const TYPE_TABS = [
  { key: 'new', label: 'New', Icon: SparklesIcon },
  { key: 'all', label: 'All' },
  { key: 'video', label: 'Videos', Icon: PlayCircleIcon },
  { key: 'guide', label: 'Guides', Icon: DocumentTextIcon },
];

// -------------------------------------------------------
// Sub-components
// -------------------------------------------------------

// Segmented control for type filter
const TypeSegment = ({ tabs, active, onChange }) => (
  <div className="inline-flex items-center flex-wrap bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
    {tabs.map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onChange(tab.key)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          active === tab.key
            ? 'bg-white dark:bg-gray-700 text-primary dark:text-primary-light shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {tab.Icon && <tab.Icon className="w-3.5 h-3.5" />}
        {tab.label}
      </button>
    ))}
  </div>
);

const EmptyState = ({ search, typeFilter, categoryFilter }) => {
  const hasFilters = search || (typeFilter !== 'all' && typeFilter !== 'new') || categoryFilter;
  const isNewTab = typeFilter === 'new' && !search && !categoryFilter;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <BookOpenIcon className="w-14 h-14 text-gray-300 dark:text-gray-600 mb-3" />
      <p className="text-base font-medium text-gray-500 dark:text-gray-400">
        {isNewTab
          ? 'No new resources'
          : hasFilters
            ? 'No resources match your filters'
            : 'No resources available yet'}
      </p>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
        {isNewTab
          ? 'Switch to All to see everything'
          : hasFilters
            ? 'Try adjusting your search or filters'
            : 'Check back soon for tutorials and guides'}
      </p>
    </div>
  );
};

// -------------------------------------------------------
// Main component
// -------------------------------------------------------

const SEARCH_MAX_LENGTH = 100;
const SEARCH_DEBOUNCE_MS = 400;

// Slug helpers — "Getting Started" ↔ "getting-started"
const toSlug = (name) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const ResourceCenter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate initial state from URL params
  const [typeFilter, setTypeFilter] = useState(() => {
    const t = searchParams.get('type');
    return VALID_TYPES.includes(t) ? t : 'new';
  });
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [pendingCategorySlug] = useState(() => searchParams.get('category') || null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('q') || '');

  // Debounce searchInput → debouncedSearch for API calls
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Active modal state
  const [videoResource, setVideoResource] = useState(null);
  const [guideResource, setGuideResource] = useState(null);

  // Fetch guide body when a guide is opened
  const { resource: guideDetail, loading: loadingGuideBody } = useResourceDetail(
    guideResource?.resourceId ?? null
  );

  // "new" and "all" both fetch all types from the API; "new" is filtered client-side
  const apiType = typeFilter === 'video' || typeFilter === 'guide' ? typeFilter : undefined;
  const { resources, categories: rawCategories, loading } = useResources({
    type: apiType,
    categoryId: categoryFilter ?? undefined,
    search: debouncedSearch || undefined,
  });

  const validCategories = useMemo(
    () => rawCategories.filter((c) => c.name?.trim()),
    [rawCategories]
  );

  // Resolve pending category slug → ID once categories have loaded
  useEffect(() => {
    if (pendingCategorySlug && validCategories.length > 0 && !categoryFilter) {
      const match = validCategories.find((c) => toSlug(c.name) === pendingCategorySlug);
      if (match) setCategoryFilter(match.categoryId);
    }
  }, [pendingCategorySlug, validCategories, categoryFilter]);

  // Sync filter state → URL search params (replace, no history spam)
  useEffect(() => {
    const params = new URLSearchParams();
    if (typeFilter !== 'new') params.set('type', typeFilter);
    if (categoryFilter) {
      const cat = validCategories.find((c) => c.categoryId === categoryFilter);
      if (cat) params.set('category', toSlug(cat.name));
    }
    if (debouncedSearch) params.set('q', debouncedSearch);
    setSearchParams(params, { replace: true });
  }, [typeFilter, categoryFilter, debouncedSearch, validCategories, setSearchParams]);

  // Client-side filter — applies "new" tab and instant search
  const filteredResources = useMemo(() => {
    let list = resources;
    if (typeFilter === 'new') list = list.filter((r) => r.isNew);
    if (searchInput) {
      const q = searchInput.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          (r.description ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [resources, searchInput, typeFilter]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (value.length <= SEARCH_MAX_LENGTH) {
      setSearchInput(value);
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setDebouncedSearch('');
  };

  const handleCardClick = useCallback((resource) => {
    if (resource.type === 'video') {
      setVideoResource(resource);
    } else {
      setGuideResource(resource);
    }
  }, []);

  const handleTypeChange = (key) => {
    setTypeFilter(key);
    setCategoryFilter(null);
  };

  const handleReset = () => {
    setTypeFilter('new');
    setCategoryFilter(null);
    setSearchInput('');
    setDebouncedSearch('');
  };

  const hasActiveFilters = typeFilter !== 'new' || categoryFilter || searchInput;

  return (
    <OathPageWrapper maxWidth="max-w-6xl">
      {/* Header */}
      <OathHeaderCard
        title="Resource Center"
        subtitle="Tutorials, guides, and how-to videos"
        icon={BookOpenIcon}
      />

      {/* Filters */}
      <OathContentCard className="mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={SEARCH_MAX_LENGTH}
              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white dark:focus:bg-gray-700 transition-all"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleSearchClear}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                aria-label="Clear search"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category dropdown */}
          {validCategories.length > 0 && (
            <div className="relative flex-shrink-0">
              <select
                value={categoryFilter ?? ''}
                onChange={(e) => setCategoryFilter(e.target.value ? Number(e.target.value) : null)}
                className={`appearance-none pl-3 pr-8 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  categoryFilter
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary/30 dark:border-primary/40 text-primary dark:text-primary-light'
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                <option value="">All Categories</option>
                {validCategories.map((cat) => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" />
            </div>
          )}

          {/* Type segment */}
          <TypeSegment tabs={TYPE_TABS} active={typeFilter} onChange={handleTypeChange} />

          {/* Reset */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-error dark:hover:text-error-light hover:bg-gray-50 dark:hover:bg-gray-800 transition-all flex-shrink-0"
              title="Reset all filters"
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}
        </div>
      </OathContentCard>

      {/* Resource grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <OathDualSpinner size="lg" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading resources...</p>
        </div>
      ) : filteredResources.length === 0 ? (
        <OathContentCard>
          <EmptyState
            search={debouncedSearch}
            typeFilter={typeFilter}
            categoryFilter={categoryFilter}
          />
        </OathContentCard>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredResources.map((resource) => (
            <ResourceCard
              key={resource.resourceId}
              resource={resource}
              onClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <VideoPlayerModal
        resource={videoResource}
        onClose={() => setVideoResource(null)}
      />
      <GuideDetailModal
        resource={guideResource}
        body={guideDetail?.body ?? null}
        loadingBody={loadingGuideBody}
        onClose={() => setGuideResource(null)}
      />
    </OathPageWrapper>
  );
};

export default ResourceCenter;
